import React, { FC } from "react";
import { JobPostCompositeStatus } from "@utils/db/JobPost/getCompositeStatus";
import { JobPostCompositeStatusValue } from "@components/Values/JobPostCompositeStatusValue";
import { DateTimeValue } from "@components/Values/DateTimeValue";
import { JobPost, JobPostAnalytics } from "@prisma/client";
import { executeAction } from "@utils/actions/executeAction";
import { useRouter } from "next/router";
import { ActionType } from "actions/actions";

interface Props {
  jobPostId: JobPost["id"];
  compositeStatus: JobPostCompositeStatus;
  isSticky?: boolean;
  createdAt?: Date;
  publishStartsAt?: Date;
  publishExpiresAt?: Date;
  stoppedAt?: Date;
  analytics?: JobPostAnalytics;
}

export const JobPostSummary: FC<Props> = ({
  jobPostId,
  compositeStatus,
  isSticky,
  createdAt,
  publishStartsAt,
  publishExpiresAt,
  stoppedAt,
  analytics,
}) => {
  const router = useRouter();

  return (
    <div className="border rounded-md">
      <div className="pb-2">
        {/* analytics */}
        <div className="mb-2 pt-4 px-4 border-b text-center">
          {/* Notice */}
          <div className="flex flex-row justify-between">
            <div className="uppercase text-xs font-bold">
              Performance Analytics
            </div>
            <span className="text-xxs text-gray-400">Updates hourly</span>
          </div>
          <div className="flex flex-row justify-between mb-1">
            {/* impressions */}
            <div className="w-1/3 py-2">
              <div className="font-medium text-3xl">
                {analytics?.impressions || 0}
              </div>
              <div className="text-xxs">
                search
                <br />
                impressions
              </div>
            </div>
            {/* detail expands */}
            <div className="w-1/3 py-2">
              <div className="font-medium text-3xl">
                {analytics?.detailViews || 0}
              </div>
              <div className="text-xxs">
                detail page
                <br />
                views
              </div>
            </div>
            {/* Apply button clicks */}
            <div className="w-1/3 py-2">
              <div className="font-medium text-3xl">
                {analytics?.applyClicks || 0}
              </div>
              <div className="text-xxs">
                apply button
                <br />
                clicks
              </div>
            </div>
          </div>
        </div>
        {/* status */}
        <div className="px-4 py-2 font-medium">
          <div className="mb-1 uppercase font-normal text-xxs">Status</div>
          <div className="flex flex-row items-center">
            <div className="text-sm">
              <JobPostCompositeStatusValue compositeStatus={compositeStatus} />
            </div>
            {compositeStatus === JobPostCompositeStatus.PUBLISHED && (
              <a
                href=""
                title="Unpublish job post"
                className="ml-4 px-2 py-0.5 text-xxs hover:bg-gray-100 border border-black rounded-full"
                onClick={async (e) => {
                  e.preventDefault();
                  await executeAction({
                    actionType: ActionType.JOB_POST__STOP,
                    jobPostId,
                  });
                  router.reload();
                }}
              >
                unpublish
              </a>
            )}
            {compositeStatus === JobPostCompositeStatus.STOPPED && (
              <a
                href=""
                title="Re-publish job post"
                className="ml-4 px-2 py-0.5 text-xxs hover:bg-gray-100 border rounded-full"
                onClick={async (e) => {
                  e.preventDefault();
                  await executeAction({
                    actionType: ActionType.JOB_POST__RESUME,
                    jobPostId,
                  });
                  router.reload();
                }}
              >
                publish
              </a>
            )}
          </div>
        </div>

        {/* isSticky */}
        <div className="px-4 py-2 font-medium">
          <div className="mb-1 uppercase font-normal text-xxs">
            Sticky on homepage?
          </div>
          <div className="flex flex-row items-center text-xs">
            {isSticky ? "Yes" : "No"}
          </div>
        </div>

        {/* created at */}
        {createdAt && (
          <div className="px-4 py-2">
            <div className="mb-1 uppercase font-normal text-xxs">Created</div>
            <div className="font-medium text-xs">
              <DateTimeValue date={createdAt} />
            </div>
          </div>
        )}
        {/* published until */}
        {(publishStartsAt || publishExpiresAt) && (
          <>
            <div className="px-4 py-2">
              <div className="mb-1 uppercase font-normal text-xxs">
                Published
              </div>
              <div className="font-medium text-xs">
                <DateTimeValue date={publishStartsAt} />
              </div>
            </div>
            {/* stopped at */}
            {compositeStatus === JobPostCompositeStatus.STOPPED && (
              <div className="px-4 py-2">
                <div className="mb-1 uppercase font-normal text-xxs">
                  Stopped
                </div>
                <div className="font-medium text-xs">
                  <DateTimeValue date={stoppedAt} />
                </div>
              </div>
            )}
            <div className="px-4 py-2">
              <div className="mb-1 uppercase font-normal text-xxs">Expires</div>
              <div className="font-medium text-xs">
                <DateTimeValue date={publishExpiresAt} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
